
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation({ isScrolled }: { isScrolled: boolean }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollToSection = (id: string) => {
    if (isHome) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      if (id) {
        // Navigate to the home page with hash for the target section
        window.location.href = `/#${id}`;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    // Convert FormData to URLSearchParams safely
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        data.append(key, value);
      } else {
        // Fallback for non‑string values
        data.append(key, String(value));
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6g6sr8r225iqdhs0l4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      // You could show a user‑friendly error message here
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'shadow-lg py-1' : 'py-2'
        }`}
        style={{
          backgroundColor: isScrolled
            ? 'rgba(220,220,220,0.97)'
            : 'rgba(210,210,210,0.92)',
        }}
      >
        <div className="px-4 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            {/* Logo on the left */}
            <Link to="/" className="flex items-center cursor-pointer flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/69b3c4875e1562fa68a140ce2d237389/1a252efef96c566f3540e448e852be3b.png"
                alt="IronCore Roofing and Exteriors"
                className={`object-contain transition-all duration-300 ${
                  isScrolled ? 'h-20' : 'h-24'
                }`}
                style={{
                  width: 'auto',
                  maxWidth: '220px',
                  filter:
                    'brightness(1.0) contrast(1.2) drop-shadow(0 0 8px rgba(0,0,0,0.2))',
                }}
              />
            </Link>

            {/* Navigation links in center */}
            <div className="hidden md:flex items-center gap-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-sm font-medium text-white hover:text-neon-green transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </button>
              <Link
                to="/services"
                className="text-sm font-medium text-white hover:text-neon-green transition-colors cursor-pointer whitespace-nowrap"
              >
                Services
              </Link>
              <button
                onClick={() => scrollToSection('about')}
                className="text-sm font-medium text-white hover:text-neon-green transition-colors cursor-pointer whitespace-nowrap"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-sm font-medium text-white hover:text-neon-green transition-colors cursor-pointer whitespace-nowrap"
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-sm font-medium text-white hover:text-neon-green transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact
              </button>
              <Link
                to="/contact"
                className="text-sm font-medium text-white hover:text-neon-green transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact Form
              </Link>
            </div>

            {/* Phone + FREE Quote on the right */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <a
                href="tel:717-280-2005"
                className="hidden md:flex items-center gap-2 text-white hover:text-neon-green transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-neon-green/15 rounded-full">
                  <i className="ri-phone-fill text-neon-green text-sm"></i>
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">
                  717-280-2005
                </span>
              </a>
              <button
                onClick={() => {
                  setShowQuoteForm(true);
                  setSubmitted(false);
                }}
                className="bg-neon-green text-matte-black px-4 py-2 rounded-md font-bold text-sm hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                FREE Quote
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowQuoteForm(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative overflow-hidden">
            {/* Header */}
            <div className="bg-matte-black px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">
                  Get Your FREE Quote
                </h2>
                <p className="text-gray-400 text-xs mt-0.5">
                  We'll get back to you within 24 hours
                </p>
              </div>
              <button
                onClick={() => setShowQuoteForm(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    Request Submitted!
                  </h3>
                  <p className="text-gray-500 text-sm mb-6">
                    Thank you! We'll be in touch shortly to discuss your project.
                  </p>
                  <button
                    onClick={() => setShowQuoteForm(false)}
                    className="bg-neon-green text-matte-black px-6 py-2 rounded-md font-bold text-sm hover:bg-green-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(717) 000-0000"
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Property Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      placeholder="123 Main St, City, PA"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Work Requested <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="work_description"
                      required
                      maxLength={500}
                      rows={3}
                      placeholder="Describe the work you need done (e.g., roof replacement, siding repair, gutters...)..."
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-neon-green text-matte-black py-2.5 rounded-md font-bold text-sm hover:bg-green-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? 'Submitting...' : 'Submit FREE Quote Request'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
